import React from 'react';
import HelpBadge from './HelpBadge';
import HelpTip from './HelpTip';
import DodTermText from '../DodTermText';
import { type PricingTierId } from '../constants/pricingTiers';
import { PLAN_PUBLIC_NAME, PLAN_RANK } from '../constants/complianceChecklists';
import {
  CMMC_LEVEL_LABELS,
  CMMC_L1_PRACTICES,
  CMMC_L2_PRACTICES,
  DOD_CAPABILITY_CATALOG,
  l1PassFail,
  practicesForLevel,
  sprsNumericScore,
  type CmmcLevel,
  type DataHandling,
  type DodCapabilityId,
  type DodCompanyRecord,
  type DodContractRecord,
  type DodEvidenceConnectors,
  type DodLevelAssessment,
  type DodPractice,
  type DodProjectRecord,
  type DodTaxonomyState,
  type PracticeDisposition,
} from '../constants/dodCmmcCatalog';

type DodLevelWorkbenchProps = {
  language: 'en' | 'es';
  taxonomy: DodTaxonomyState;
  dispositions: Record<string, PracticeDisposition>;
  assessment: DodLevelAssessment | null;
  connectors: DodEvidenceConnectors;
  classifyBusy: boolean;
  canUseAi: boolean;
  onTaxonomyChange: (next: DodTaxonomyState) => void;
  onDispositionChange: (practiceId: string, disposition: PracticeDisposition) => void;
  onConnectorsChange: (next: DodEvidenceConnectors) => void;
  onClassify: () => void;
  /** Hide duplicate scope editors when the parent page already has those pickers. */
  hideScopeEditors?: boolean;
  /** ids is the company file. level is the CMMC worksheet. all keeps both. */
  part?: 'all' | 'ids' | 'level';
  plan?: PricingTierId;
};

const DISPOSITIONS: Array<{ value: PracticeDisposition; en: string; es: string; l2Only?: boolean }> = [
  { value: 'met', en: 'Yes', es: 'Sí' },
  { value: 'partial', en: 'Partial', es: 'Parcial' },
  { value: 'not_met', en: 'No', es: 'No' },
  { value: 'na', en: 'N/A', es: 'N/A' },
  { value: 'poam', en: 'POA&M', es: 'POA&M', l2Only: true },
];

const DATA_HANDLING: Array<{ value: DataHandling; en: string; es: string }> = [
  { value: 'none', en: 'Not sure yet', es: 'Aún no sé' },
  { value: 'fci', en: 'Everyday government papers (FCI)', es: 'Papeles cotidianos del gobierno (FCI)' },
  { value: 'cui', en: 'Extra-secret papers (CUI)', es: 'Papeles extra secretos (CUI)' },
  { value: 'classified', en: 'Classified (do not use yet)', es: 'Clasificado (no usar aún)' },
];

const copy = (language: 'en' | 'es', en: string, es: string): string => (language === 'es' ? es : en);

const activeCompany = (taxonomy: DodTaxonomyState): DodCompanyRecord | undefined => (
  taxonomy.companies.find((row) => row.id === taxonomy.activeCompanyId)
);

const activeProject = (taxonomy: DodTaxonomyState): DodProjectRecord | undefined => (
  taxonomy.projects.find((row) => row.id === taxonomy.activeProjectId)
);

const activeContract = (taxonomy: DodTaxonomyState): DodContractRecord | undefined => (
  taxonomy.contracts.find((row) => row.id === taxonomy.activeContractId)
);

const patchCompany = (
  taxonomy: DodTaxonomyState,
  companyId: string,
  patch: Partial<DodCompanyRecord>,
): DodTaxonomyState => ({
  ...taxonomy,
  companies: taxonomy.companies.map((row) => (row.id === companyId ? { ...row, ...patch } : row)),
});

const patchProject = (
  taxonomy: DodTaxonomyState,
  projectId: string,
  patch: Partial<DodProjectRecord>,
): DodTaxonomyState => ({
  ...taxonomy,
  projects: taxonomy.projects.map((row) => (row.id === projectId ? { ...row, ...patch } : row)),
});

const patchContract = (
  taxonomy: DodTaxonomyState,
  contractId: string,
  patch: Partial<DodContractRecord>,
): DodTaxonomyState => ({
  ...taxonomy,
  contracts: taxonomy.contracts.map((row) => (row.id === contractId ? { ...row, ...patch } : row)),
});

const practicesByFamily = (practices: DodPractice[]): Array<{ family: string; rows: DodPractice[] }> => {
  const order: string[] = [];
  const grouped = new Map<string, DodPractice[]>();
  practices.forEach((practice) => {
    if (!grouped.has(practice.family)) {
      order.push(practice.family);
      grouped.set(practice.family, []);
    }
    grouped.get(practice.family)?.push(practice);
  });
  return order.map((family) => ({ family, rows: grouped.get(family) || [] }));
};

const PracticeRow: React.FC<{
  practice: DodPractice;
  disposition: PracticeDisposition;
  language: 'en' | 'es';
  onChange: (value: PracticeDisposition) => void;
}> = ({ practice, disposition, language, onChange }) => {
  const code = practice.nistId || practice.farId || practice.id;
  return (
    <div className={`dod-practice-row status-${disposition}`}>
      <div className="dod-practice-copy">
        <code>{code}</code>
        <strong>{practice.title}</strong>
      </div>
      <div className="dod-practice-dispositions" role="group" aria-label={practice.title}>
        {DISPOSITIONS.filter((option) => !option.l2Only || practice.level === 'l2').map((option) => (
          <button
            key={option.value}
            type="button"
            className={disposition === option.value ? 'is-active' : ''}
            onClick={() => onChange(option.value)}
          >
            {language === 'es' ? option.es : option.en}
          </button>
        ))}
      </div>
    </div>
  );
};

const DodLevelWorkbench: React.FC<DodLevelWorkbenchProps> = ({
  language,
  taxonomy: taxonomyProp,
  dispositions,
  assessment,
  connectors,
  classifyBusy,
  canUseAi,
  onTaxonomyChange,
  onDispositionChange,
  onConnectorsChange,
  onClassify,
  hideScopeEditors = false,
  part = 'all',
  plan = 'enterprise',
}) => {
  const taxonomy = taxonomyProp;
  const company = activeCompany(taxonomy);
  const project = activeProject(taxonomy);
  const contract = activeContract(taxonomy);
  const classifyReady = Boolean(
    String(project?.description || '').trim() || String(contract?.clauseText || '').trim(),
  );
  const unlocked = new Set(assessment?.unlocks || []);
  const planAllowsL2 = PLAN_RANK[plan] >= PLAN_RANK.advanced;
  const planAllowsCuiEnclave = PLAN_RANK[plan] >= PLAN_RANK.enterprise;
  const showL1 = unlocked.has('l1_15_practices') || assessment?.contractLevel === 'l1';
  const showL2 = planAllowsL2 && unlocked.has('l2_110_practices');
  const showSprs = planAllowsL2 && unlocked.has('sprs_numeric_scoring');
  const l1Score = l1PassFail(dispositions);
  const sprs = sprsNumericScore(dispositions);
  const visiblePractices = showL2
    ? practicesForLevel('l2')
    : showL1
      ? CMMC_L1_PRACTICES
      : [];
  const families = practicesByFamily(visiblePractices);
  const contractLevel: CmmcLevel = assessment?.contractLevel || 'none';
  const capabilityIds = Object.keys(DOD_CAPABILITY_CATALOG) as DodCapabilityId[];
  const inScopeIds = capabilityIds.filter((id) => unlocked.has(id));
  const laterIds = capabilityIds.filter((id) => !unlocked.has(id));
  const showConnectors = planAllowsCuiEnclave && (
    unlocked.has('gcc_high_entra_intune_evidence') || unlocked.has('preveil_cui_mailbox')
  );
  const wantsL2 = unlocked.has('l2_110_practices') || unlocked.has('sprs_numeric_scoring');
  const wantsEnclave = unlocked.has('gcc_high_entra_intune_evidence') || unlocked.has('preveil_cui_mailbox');

  const commit = (next: DodTaxonomyState) => onTaxonomyChange(next);
  const showIds = part !== 'level';
  const showLevel = part !== 'ids';

  if (!company) {
    return null;
  }

  const pathSeen = new Set<string>();
  const pathSteps = showLevel
    ? [
      copy(
        language,
        'The contract decides the level. Everyday contract data (FCI) stays on CMMC Level 1. Extra-secret unclassified data (CUI) is Level 2.',
        'El contrato decide el nivel. Los datos cotidianos del contrato (FCI) quedan en CMMC Nivel 1. Los datos no clasificados extra secretos (CUI) son Nivel 2.',
      ),
      copy(
        language,
        'Mark each practice met or not met. Level 1 is pass or fail. There is no partial credit.',
        'Marque cada práctica como cumplida o no cumplida. El Nivel 1 es cumple o no cumple. No hay crédito parcial.',
      ),
      copy(
        language,
        'A senior official affirms the result in SPRS. Level 1 is an annual self-assessment. Level 2 also carries a numeric score, and that affirmation is still entered in SPRS.',
        'Un funcionario superior afirma el resultado en SPRS. El Nivel 1 es una autoevaluación anual. El Nivel 2 también lleva un puntaje numérico, y esa afirmación se ingresa en SPRS.',
      ),
      copy(
        language,
        'NextCompli does not file SPRS and does not certify CMMC. When a contract requires a third-party C3PAO assessment, that step happens outside this page.',
        'NextCompli no presenta SPRS ni certifica CMMC. Cuando un contrato exige una evaluación de un C3PAO, ese paso ocurre fuera de esta página.',
      ),
    ]
    : [];

  return (
    <div className="dod-level-workbench" aria-label={part === 'ids' ? copy(language, 'Company IDs', 'Identificadores') : copy(language, 'CMMC level workbench', 'Banco de nivel CMMC')}>
      {!hideScopeEditors ? (
        <div className="dod-level-workbench-header">
          <h3>
            {copy(language, 'This company’s DoD ID card', 'Credencial DoD de esta empresa')}
            <HelpBadge label={copy(language, 'What this box is for', 'Para qué es esta caja')}>
              <p>
                {copy(
                  language,
                  'This is the company’s ID card for Defense work. Legal name is its real grown-up name. EIN is its tax number. UEI and CAGE are extra ID numbers the government hands out later — leave those blank until SAM.gov gives them to you. Extra lock-and-key tools only appear if this job touches extra-secret papers.',
                  'Esta es la credencial de la empresa para trabajo de Defensa. El nombre legal es su nombre de verdad. El EIN es su número de impuestos. UEI y CAGE los da el gobierno más tarde — déjelos vacíos hasta que SAM.gov se los entregue. Las herramientas extra solo aparecen si este trabajo toca papeles más secretos.',
                )}
              </p>
            </HelpBadge>
          </h3>
        </div>
      ) : null}

      {showLevel && (plan === 'basic' || (wantsL2 && !planAllowsL2) || (wantsEnclave && !planAllowsCuiEnclave)) ? (
        <aside className="compliance-console-notice" role="status">
          <strong>
            {copy(
              language,
              `${PLAN_PUBLIC_NAME[plan]} DoD coverage`,
              `Cobertura DoD de ${PLAN_PUBLIC_NAME[plan]}`,
            )}
          </strong>
          <p>
            <DodTermText
              language={language}
              text={plan === 'basic'
                ? copy(
                  language,
                  'CompliCore stays on FCI / CMMC Level 1 (15 FAR 52.204-21 practices and SPRS affirmation). CUI, Level 2, SSP/POA&M, and GCC High / PreVeil pointers need CompliAdvantage or CompliComplete. Do not upload CUI here.',
                  'CompliCore se queda en FCI / CMMC Nivel 1 (15 prácticas FAR 52.204-21 y afirmación SPRS). CUI, Nivel 2, SSP/POA&M y punteros GCC High / PreVeil necesitan CompliAdvantage o CompliComplete. No cargue CUI aquí.',
                )
                : copy(
                  language,
                  'CompliAdvantage tracks CMMC Level 2 and DFARS 252.204-7012 without storing CUI. GCC High / PreVeil CUI mailbox pointers and SSP export are CompliComplete.',
                  'CompliAdvantage rastrea CMMC Nivel 2 y DFARS 252.204-7012 sin almacenar CUI. Los punteros de buzón CUI GCC High / PreVeil y la exportación SSP son CompliComplete.',
                )}
            />
          </p>
        </aside>
      ) : null}

      <div className={`dod-taxonomy-grid${hideScopeEditors || !project ? ' dod-taxonomy-grid-1' : (contract ? '' : ' dod-taxonomy-grid-2')}`}>
        {showIds ? (
        <>
        <fieldset>
          <legend>
            {copy(language, 'Company name and IDs', 'Nombre e identificadores')}
            <HelpBadge label={copy(language, 'What these boxes mean', 'Qué significan estas cajas')}>
              <p>
                {copy(
                  language,
                  'Legal name: the name on the tax letter. EIN: the tax number. UEI and CAGE: government IDs you get after signing up on SAM.gov. Do not make up UEI or CAGE. DUNS is not required.',
                  'Nombre legal: el de la carta de impuestos. EIN: el número de impuestos. UEI y CAGE: identificadores del gobierno después de registrarse en SAM.gov. No los invente. DUNS no es obligatorio.',
                )}
              </p>
            </HelpBadge>
          </legend>
          <label>
            {copy(language, 'Legal name', 'Razón social')}
            <input
              value={company.name || ''}
              onChange={(event) => commit(patchCompany(taxonomy, company.id, { name: event.target.value }))}
            />
          </label>
          <label>
            EIN
            <input
              value={company.ein || ''}
              onChange={(event) => commit(patchCompany(taxonomy, company.id, { ein: event.target.value }))}
            />
          </label>
          <label>
            UEI
            <input
              value={company.uei || ''}
              placeholder={copy(language, 'Pending SAM', 'SAM pendiente')}
              onChange={(event) => commit(patchCompany(taxonomy, company.id, { uei: event.target.value }))}
            />
          </label>
          <label>
            CAGE
            <input
              value={company.cage || ''}
              placeholder={copy(language, 'Do not invent', 'No inventar')}
              onChange={(event) => commit(patchCompany(taxonomy, company.id, { cage: event.target.value }))}
            />
          </label>
        </fieldset>

        {project && !hideScopeEditors ? (
          <fieldset>
            <legend>
              {copy(language, 'What papers this job touches', 'Qué papeles toca este trabajo')}
              <HelpBadge label={copy(language, 'What this means', 'Qué significa')}>
                <p>
                  {copy(
                    language,
                    'Everyday government papers (FCI) stay on the simple checklist. Extra-secret papers (CUI) unlock extra lockboxes. Pick the kind of papers this job actually uses — do not guess up.',
                    'Los papeles cotidianos del gobierno (FCI) usan la lista simple. Los papeles extra secretos (CUI) abren cajas extra. Elija los papeles que este trabajo usa de verdad — no adivine hacia arriba.',
                  )}
                </p>
              </HelpBadge>
            </legend>
            <label>
              {copy(language, 'Name', 'Nombre')}
              <input
                value={project.name || ''}
                onChange={(event) => commit(patchProject(taxonomy, project.id, { name: event.target.value }))}
              />
            </label>
            <label>
              {copy(language, 'Data handling', 'Manejo de datos')}
              <select
                value={project.dataHandling || 'none'}
                onChange={(event) => commit(patchProject(taxonomy, project.id, {
                  dataHandling: event.target.value as DataHandling,
                }))}
              >
                {DATA_HANDLING.map((option) => (
                  <option key={option.value} value={option.value}>
                    {language === 'es' ? option.es : option.en}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {copy(language, 'Description', 'Descripción')}
              <textarea
                rows={4}
                value={project.description || ''}
                onChange={(event) => commit(patchProject(taxonomy, project.id, { description: event.target.value }))}
              />
            </label>
          </fieldset>
        ) : null}
        </>
        ) : null}

        {showLevel && project && hideScopeEditors ? (
          <fieldset>
            <legend>
              {copy(language, 'Job notes for level calculation', 'Notas del trabajo para calcular el nivel')}
              <HelpBadge label={copy(language, 'Why this box', 'Por qué esta caja')}>
                <p>
                  {copy(
                    language,
                    'A short note about the papers this job uses. Needed before Calculate level. Do not paste CUI into this commercial console.',
                    'Una nota corta sobre los papeles que usa este trabajo. Se necesita antes de Calcular nivel. No pegue CUI en esta consola comercial.',
                  )}
                </p>
              </HelpBadge>
            </legend>
            <label>
              {copy(language, 'Description', 'Descripción')}
              <textarea
                rows={3}
                value={project.description || ''}
                onChange={(event) => commit(patchProject(taxonomy, project.id, { description: event.target.value }))}
              />
            </label>
          </fieldset>
        ) : null}

        {showLevel && contract && !hideScopeEditors ? (
          <fieldset>
            <legend>
              {copy(language, 'The job posting', 'El aviso de trabajo')}
              <HelpBadge label={copy(language, 'What to paste here', 'Qué pegar aquí')}>
                <p>
                  {copy(
                    language,
                    'Paste the part of the job posting that talks about cybersecurity. The tool looks for clues about everyday papers vs extra-secret papers. You do not have to know the clause numbers.',
                    'Pegue la parte del aviso que habla de ciberseguridad. La herramienta busca pistas de papeles cotidianos vs extra secretos. No necesita saber los números de cláusula.',
                  )}
                </p>
              </HelpBadge>
            </legend>
            <label>
              {copy(language, 'Name / solicitation', 'Nombre / aviso')}
              <input
                value={contract.name || ''}
                onChange={(event) => commit(patchContract(taxonomy, contract.id, { name: event.target.value }))}
              />
            </label>
            <label>
              {copy(language, 'Agency', 'Agencia')}
              <input
                value={contract.agency || ''}
                onChange={(event) => commit(patchContract(taxonomy, contract.id, { agency: event.target.value }))}
              />
            </label>
            <label>
              {copy(language, 'Clause / PWS extract', 'Extracto de cláusulas / PWS')}
              <textarea
                rows={4}
                value={contract.clauseText || ''}
                placeholder={copy(
                  language,
                  'Paste FAR 52.204-21, DFARS 252.204-7012, CMMC level, CUI markings…',
                  'Pegue FAR 52.204-21, DFARS 252.204-7012, nivel CMMC, marcas CUI…',
                )}
                onChange={(event) => commit(patchContract(taxonomy, contract.id, { clauseText: event.target.value }))}
              />
            </label>
          </fieldset>
        ) : null}

        {showLevel && contract && hideScopeEditors ? (
          <fieldset>
            <legend>
              {copy(language, 'Clause extract', 'Extracto de cláusulas')}
              <HelpBadge label={copy(language, 'What to paste here', 'Qué pegar aquí')}>
                <p>
                  {copy(
                    language,
                    'Paste only the cybersecurity part of the posting. Do not paste CUI into this commercial console.',
                    'Pegue solo la parte de ciberseguridad del aviso. No pegue CUI en esta consola comercial.',
                  )}
                </p>
              </HelpBadge>
            </legend>
            <label>
              {copy(language, 'Clause / PWS extract', 'Extracto de cláusulas / PWS')}
              <textarea
                rows={3}
                value={contract.clauseText || ''}
                placeholder={copy(
                  language,
                  'Paste FAR 52.204-21, DFARS 252.204-7012, CMMC level…',
                  'Pegue FAR 52.204-21, DFARS 252.204-7012, nivel CMMC…',
                )}
                onChange={(event) => commit(patchContract(taxonomy, contract.id, { clauseText: event.target.value }))}
              />
            </label>
          </fieldset>
        ) : null}
      </div>

      {pathSteps.length ? (
        <ol className="dod-cmmc-path">
          {pathSteps.map((step) => (
            <li key={step}>
              <DodTermText text={step} language={language} seen={pathSeen} />
            </li>
          ))}
        </ol>
      ) : null}

      {showLevel && project ? (
        <div className="dod-classify-row">
          <button
            type="button"
            className="dashboard-btn dashboard-btn-primary"
            disabled={!classifyReady || classifyBusy}
            onClick={onClassify}
          >
            {classifyBusy
              ? copy(language, 'Classifying…', 'Clasificando…')
              : copy(language, canUseAi ? 'Calculate level (rules + AI)' : 'Calculate level (rules)', canUseAi ? 'Calcular nivel (reglas + IA)' : 'Calcular nivel (reglas)')}
          </button>
          <HelpTip
            text={
              classifyReady
                ? copy(
                  language,
                  'Ready. Level 1 is pass/fail only. A numeric SPRS score is withheld until CUI / Level 2.',
                  'Listo. Nivel 1 es cumple/no cumple. El SPRS numérico se reserva hasta CUI / Nivel 2.',
                )
                : copy(
                  language,
                  'Add a project description or contract clause extract, then calculate. This does not file SPRS or certify CMMC.',
                  'Agregue una descripción de proyecto o un extracto de cláusulas y luego calcule. No presenta SPRS ni certifica CMMC.',
                )
            }
          />
        </div>
      ) : null}

      {assessment ? (
        <div className="dod-assessment-banner" data-level={contractLevel}>
          <div>
            <strong>{CMMC_LEVEL_LABELS[contractLevel][language]}</strong>
            <HelpTip text={assessment.rationale} />
            <span>
              {copy(language, 'Company readiness', 'Preparación de empresa')}: {CMMC_LEVEL_LABELS[assessment.companyLevel][language]}
            </span>
            <span>
              {copy(language, 'Data', 'Datos')}: {assessment.dataHandling.toUpperCase()} · {copy(language, 'Path', 'Ruta')}: {assessment.assessmentPath} · {assessment.source === 'ai' ? 'AI' : copy(language, 'Rules', 'Reglas')}
            </span>
          </div>
        </div>
      ) : null}

      {assessment && contractLevel === 'l1' && !showSprs ? (
        <p className="dod-withheld">
          {copy(language, 'SPRS numeric withheld', 'SPRS numérico reservado')}
          <HelpTip text={copy(language, 'Do not generate a −203 to 110 score for FCI-only work.', 'No genere un puntaje −203 a 110 para trabajo solo FCI.')} />
        </p>
      ) : null}

      {assessment && inScopeIds.length ? (
        <div className="dod-capability-chips" aria-label={copy(language, 'Tools in scope', 'Herramientas en alcance')}>
          {inScopeIds.map((id) => {
            const capability = DOD_CAPABILITY_CATALOG[id];
            return (
              <span key={id} className="dod-capability-chip is-open">
                {capability.title}
                <HelpTip
                  text={capability.when}
                  href={id === 'preveil_cui_mailbox' ? capability.partnerHint : undefined}
                  hrefLabel={id === 'preveil_cui_mailbox' ? 'PreVeil' : undefined}
                />
              </span>
            );
          })}
        </div>
      ) : null}

      {assessment && laterIds.length ? (
        <details className="dod-compact-acc">
          <summary>
            {copy(language, `Not in scope yet (${laterIds.length})`, `Aún fuera de alcance (${laterIds.length})`)}
          </summary>
          <div className="dod-capability-chips">
            {laterIds.map((id) => {
              const capability = DOD_CAPABILITY_CATALOG[id];
              return (
                <span key={id} className="dod-capability-chip is-locked">
                  {capability.title}
                  <HelpTip text={capability.when} />
                </span>
              );
            })}
          </div>
        </details>
      ) : null}

      {showL1 || showSprs ? (
        <div className="dod-score-row dod-score-row-compact">
          {showL1 ? (
            <div className="dod-score-card">
              <strong>
                {copy(language, 'Level 1', 'Nivel 1')}
                <HelpTip text={copy(language, '15 FAR 52.204-21 practices. Pass/fail only — no POA&M credit at Level 1.', '15 prácticas FAR 52.204-21. Solo cumple/no cumple — sin crédito POA&M en Nivel 1.')} />
              </strong>
              <span>{l1Score.met}/{l1Score.total}</span>
              <em>{l1Score.passing ? copy(language, 'Ready to affirm', 'Listo para afirmar') : copy(language, 'Not ready', 'No listo')}</em>
            </div>
          ) : null}
          {showSprs ? (
            <div className="dod-score-card">
              <strong>
                SPRS
                <HelpTip text={copy(language, 'NIST 800-171 numeric score (−203 to 110). Only for CUI / Level 2.', 'Puntaje numérico NIST 800-171 (−203 a 110). Solo para CUI / Nivel 2.')} />
              </strong>
              <span>{sprs.score}</span>
              <em>{sprs.met} MET · {sprs.poam} POA&M</em>
            </div>
          ) : null}
        </div>
      ) : null}

      {showConnectors ? (
        <fieldset className="dod-connectors">
          <legend>
            {copy(language, 'Evidence pointers', 'Punteros de evidencia')}
            <HelpTip text={copy(language, 'Pointers only. Do not ingest CUI into commercial NextCompli.', 'Solo punteros. No ingiera CUI en NextCompli comercial.')} />
          </legend>
          <label>
            <input
              type="checkbox"
              checked={connectors.gccHigh}
              disabled={!unlocked.has('gcc_high_entra_intune_evidence')}
              onChange={(event) => onConnectorsChange({ ...connectors, gccHigh: event.target.checked })}
            />
            GCC High tenant
          </label>
          <label>
            <input
              type="checkbox"
              checked={connectors.entra}
              disabled={!unlocked.has('gcc_high_entra_intune_evidence')}
              onChange={(event) => onConnectorsChange({ ...connectors, entra: event.target.checked })}
            />
            Entra ID export
          </label>
          <label>
            <input
              type="checkbox"
              checked={connectors.intune}
              disabled={!unlocked.has('gcc_high_entra_intune_evidence')}
              onChange={(event) => onConnectorsChange({ ...connectors, intune: event.target.checked })}
            />
            Intune compliance
          </label>
          <label>
            <input
              type="checkbox"
              checked={connectors.preveilMailbox}
              disabled={!unlocked.has('preveil_cui_mailbox')}
              onChange={(event) => onConnectorsChange({ ...connectors, preveilMailbox: event.target.checked })}
            />
            {copy(language, 'PreVeil / GCC High CUI mailbox', 'Buzón CUI PreVeil / GCC High')}
          </label>
        </fieldset>
      ) : null}

      {families.length > 0 ? (
        <details className="dod-compact-acc" open>
          <summary>
            {showL2
              ? copy(language, `Practices (${CMMC_L1_PRACTICES.length} L1 + ${CMMC_L2_PRACTICES.length} L2)`, `Prácticas (${CMMC_L1_PRACTICES.length} N1 + ${CMMC_L2_PRACTICES.length} N2)`)
              : copy(language, `Level 1 practices (${CMMC_L1_PRACTICES.length})`, `Prácticas de Nivel 1 (${CMMC_L1_PRACTICES.length})`)}
          </summary>
          <div className="dod-practice-families">
            {families.map((family) => (
              <section key={family.family}>
                <h5>{family.family}</h5>
                {family.rows.map((practice) => (
                  <PracticeRow
                    key={practice.id}
                    practice={practice}
                    disposition={dispositions[practice.id] || 'not_started'}
                    language={language}
                    onChange={(value) => onDispositionChange(practice.id, value)}
                  />
                ))}
              </section>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
};

export default DodLevelWorkbench;
